import { RadioGroup } from '@headlessui/react'
import { debounce } from 'lodash'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import styled from 'styled-components'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Input } from '@/components/Input.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  LocationCard,
  LocationRadio,
} from '@/components/templates/LocationCard.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { BodyM, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { selectAppointmentTypePath, selectDoctorPath } from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'

import { VelthuisFormStep } from '../../../utils/VelthuisFormSteps.ts'
import { LoaderData } from './actions.ts'

const StyledInput = styled(Input)`
  margin-bottom: 24px;
`

const ShowingDistanceDiv = styled(BodyM)`
  margin-bottom: 12px;
`

const SelectClinic = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectClinic',
  })
  const navigate = useNavigate()
  const { appointmentType, category } = useParams() as {
    category: string
    appointmentType: string
  }
  const { selectedLocation, setSelectedLocation } = useAppointmentStore(
    (state) => ({
      selectedLocation: state.selectedLocation,
      setSelectedLocation: state.setSelectedLocation,
    }),
  )
  const { loadTime, locations, postalCode, postalCodeInvalid } =
    useLoaderData() as LoaderData

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectClinic,
    { loadTime },
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const debouncedSetPostalCode = debounce((value: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (value) {
      newSearchParams.set('postalCode', value)
    } else {
      newSearchParams.delete('postalCode')
    }
    setSearchParams(newSearchParams)
  }, 1000)

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    debouncedSetPostalCode(value)
  }

  useEffect(() => {
    if (postalCodeInvalid) {
      trackFormError({
        error: t('zipCodeInvalid'),
      })
    }
  }, [postalCodeInvalid])

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          onClick={() => {
            navigate(
              selectAppointmentTypePath({
                category: category!,
              }),
            )
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={2} />

        <StyledInput
          label={t('zipCodeLabel')}
          defaultValue={postalCode || ''}
          onChange={handlePostalCodeChange}
          autoFocus={true}
          onFocus={(e) => {
            trackFormInteraction({
              action: 'click',
              formFieldName: 'postalCode',
              formFieldValue: e.target.value,
            })
          }}
          errorMessage={postalCodeInvalid ? t('zipCodeInvalid') : undefined}
        />
        <ScrollableContent>
          {postalCode && !postalCodeInvalid && (
            <ShowingDistanceDiv>{t('showingDistance')}</ShowingDistanceDiv>
          )}
          <RadioGroup
            name="selectedLocation"
            value={selectedLocation}
            onChange={(value) => {
              setSelectedLocation(value)
              trackFormInteraction({
                action: 'click',
                formFieldName: 'location',
                formFieldValue: value.name,
                formFieldValues: locations?.map(
                  (location) => location.name || '',
                ),
              })
            }}
          >
            {locations?.map((location) => (
              <LocationRadio value={location} key={location.emmaId}>
                <LocationCard location={location} />
              </LocationRadio>
            ))}
          </RadioGroup>
        </ScrollableContent>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            disabled={!selectedLocation}
            onClick={() => {
              if (!selectedLocation) return
              navigate(
                selectDoctorPath({
                  appointmentType: appointmentType,
                  category: category,
                  locationId: selectedLocation.emmaId,
                }),
              )
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('selectDoctor')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectClinic }
